import React, { useState, useEffect, useRef } from 'react';
import { budgetAPI } from '../../services/api';
import './SpendingReportPanel.css';

/**
 * Upload PayPal CSV + generate the consolidated HTML spending report
 * from already-uploaded bank statements.
 */
const SpendingReportPanel = ({ houseId, statementCount = 0 }) => {
  const [paypalStatus, setPaypalStatus] = useState({ uploaded: false });
  const [generating, setGenerating] = useState(false);
  const [uploadingPaypal, setUploadingPaypal] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const paypalInputRef = useRef(null);

  const loadPaypalStatus = async () => {
    if (!houseId) return;
    try {
      const res = await budgetAPI.getPaypalStatus(houseId);
      setPaypalStatus(res.data || res);
    } catch (e) {
      // Non-fatal
      setPaypalStatus({ uploaded: false });
    }
  };

  useEffect(() => {
    loadPaypalStatus();
  }, [houseId]);

  const handlePaypalUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPaypal(true);
    setError(null);
    setMessage(null);
    try {
      await budgetAPI.uploadPaypalCsv(houseId, file);
      setMessage(`PayPal CSV uploaded: ${file.name}`);
      await loadPaypalStatus();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploadingPaypal(false);
      if (paypalInputRef.current) paypalInputRef.current.value = '';
    }
  };

  const handleDeletePaypal = async () => {
    if (!window.confirm('Remove the uploaded PayPal CSV?')) return;
    try {
      await budgetAPI.deletePaypalCsv(houseId);
      setPaypalStatus({ uploaded: false });
      setMessage('PayPal CSV removed');
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleGenerate = async () => {
    if (!houseId) {
      setError('Select a house first');
      return;
    }
    if (statementCount === 0) {
      setError('Upload at least one bank statement PDF first');
      return;
    }
    setGenerating(true);
    setError(null);
    setMessage(null);
    try {
      const res = await budgetAPI.generateSpendingReport(houseId);
      const html = typeof res.data === 'string' ? res.data : String(res.data);
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke later so the new tab can load
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setMessage('Report opened in a new tab');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : err.message || 'Failed to generate report'
      );
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="spending-report-panel">
      <div className="spending-report-header">
        <div>
          <h4>Spending analysis report</h4>
          <p>
            Builds a full HTML report (month-by-month + tips) from your uploaded
            LCL / BoursoBank PDFs. Optionally add a PayPal activity CSV for merchant detail.
          </p>
        </div>
        <button
          type="button"
          className="btn-generate-report"
          onClick={handleGenerate}
          disabled={generating || statementCount === 0}
        >
          {generating ? 'Generating…' : 'Generate report'}
        </button>
      </div>

      <div className="spending-report-paypal">
        <div className="paypal-status">
          <span className={`dot ${paypalStatus.uploaded ? 'on' : ''}`} />
          {paypalStatus.uploaded
            ? 'PayPal CSV ready'
            : 'No PayPal CSV yet (optional)'}
        </div>
        <div className="paypal-actions">
          <input
            ref={paypalInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handlePaypalUpload}
            disabled={uploadingPaypal}
            id="paypal-csv-input"
            className="hidden-file"
          />
          <label htmlFor="paypal-csv-input" className="btn-paypal-upload">
            {uploadingPaypal ? 'Uploading…' : 'Upload PayPal CSV'}
          </label>
          {paypalStatus.uploaded && (
            <button type="button" className="btn-paypal-remove" onClick={handleDeletePaypal}>
              Remove
            </button>
          )}
        </div>
      </div>

      {error && <div className="spending-report-error">{error}</div>}
      {message && <div className="spending-report-ok">{message}</div>}
    </div>
  );
};

export default SpendingReportPanel;
